import { useEffect, useMemo, useRef, useState } from "react";

import { trpc } from "../../../../common/helpers/trpc";
import { coerceLegacyTicketingStepKey, LiveEditorStep, LiveEditorStepKey } from "../Steps/LiveEditorStep.enum";

import { useCoalescedStepWrite } from "./useCoalescedStepWrite";

import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/common/types/trpc";

export type FurthestStepProgress = {
  reached: LiveEditorStep; // The furthest step the user has navigated to
  saved: LiveEditorStep; // The furthest step the user has saved
};

type UseStepProgressParams = {
  currentStepKey: LiveEditorStepKey;
  currentStepIndex: LiveEditorStep;
  apiFormObject: TicketingOutput | DonationFormWithUserOrOrgAccessOutput;
  getIndexStepFromKey: (key: LiveEditorStepKey) => LiveEditorStep;
  formType: "ticketing" | "donation-form";
};

export const useStepProgress = ({
  currentStepIndex,
  apiFormObject,
  getIndexStepFromKey,
  currentStepKey,
  formType,
}: UseStepProgressParams) => {
  const saveTicketingStep = trpc.updateTicketingFurthestStepReachedInLiveEditor.useMutation();
  const saveDonationFormStep = trpc.updateDonationFormFurthestStepReachedInLiveEditor.useMutation();
  const saveTicketingCompletedStep = trpc.updateTicketingStepCompletedInLiveEditor.useMutation();
  const saveDonationCompletedStep = trpc.updateDonationFormStepCompletedInLiveEditor.useMutation();
  const saveFurthestStepReached = useCoalescedStepWrite((stepKey) =>
    formType === "ticketing"
      ? saveTicketingStep.mutateAsync({ ticketingId: apiFormObject.id, liveEditorStep: stepKey })
      : saveDonationFormStep.mutateAsync({ donationFormId: apiFormObject.id, liveEditorStep: stepKey })
  );
  const saveFurthestStepCompleted = useCoalescedStepWrite((stepKey) =>
    formType === "ticketing"
      ? saveTicketingCompletedStep.mutateAsync({ ticketingId: apiFormObject.id, liveEditorStep: stepKey })
      : saveDonationCompletedStep.mutateAsync({ donationFormId: apiFormObject.id, liveEditorStep: stepKey })
  );

  const previousStepIndexRef = useRef<LiveEditorStep | null>(null);

  const apiReachedStepIndex = useMemo(() => {
    const apiStepKey = (apiFormObject.liveFormEditorStep || "title") as LiveEditorStepKey;
    return getIndexStepFromKey(coerceLegacyTicketingStepKey(apiStepKey, formType));
  }, [apiFormObject.liveFormEditorStep, getIndexStepFromKey, formType]);

  const apiFurthestStepSavedIndex = useMemo(() => {
    const savedStepKey = (apiFormObject.editorFurthestStepSaved ||
      apiFormObject.liveFormEditorStep ||
      "title") as LiveEditorStepKey;
    return getIndexStepFromKey(coerceLegacyTicketingStepKey(savedStepKey, formType));
  }, [apiFormObject.liveFormEditorStep, apiFormObject.editorFurthestStepSaved, getIndexStepFromKey, formType]);

  const [furthestStepReached, setFurthestStepReached] = useState<LiveEditorStep>(apiReachedStepIndex);
  const [furthestStepSaved, setFurthestStepSaved] = useState<LiveEditorStep>(apiFurthestStepSavedIndex);

  useEffect(() => {
    setFurthestStepReached((prev) => (apiReachedStepIndex > prev ? apiReachedStepIndex : prev));
  }, [apiReachedStepIndex]);

  useEffect(() => {
    setFurthestStepSaved((prev) => (apiFurthestStepSavedIndex > prev ? apiFurthestStepSavedIndex : prev));
  }, [apiFurthestStepSavedIndex]);

  useEffect(() => {
    if (previousStepIndexRef.current === currentStepIndex) {
      return;
    }

    if (currentStepIndex > furthestStepReached) {
      setFurthestStepReached(currentStepIndex);
      saveFurthestStepReached(currentStepIndex, currentStepKey);
    }

    previousStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex, furthestStepReached, currentStepKey, saveFurthestStepReached]);

  const markStepAsSaved = (stepIndex: LiveEditorStep, stepKey: LiveEditorStepKey) => {
    if (stepIndex > furthestStepSaved) {
      saveFurthestStepCompleted(stepIndex, stepKey);
    }
    setFurthestStepSaved((prev) => (stepIndex > prev ? stepIndex : prev));
  };

  return {
    stepProgress: {
      reached: furthestStepReached,
      saved: furthestStepSaved,
    },
    markStepAsSaved,
  };
};
